<script setup lang="ts">
  import CropIcon from '@/components/icons/CropIcon.vue'
  import { RotateCwSquare, Scissors, SlidersHorizontal, X } from 'lucide-vue-next'
  import { nextTick, onMounted, onUnmounted, ref, toRefs, watch } from 'vue'

  // Components
  import ImageEditorCanvas from './components/ImageEditorCanvas.vue'
  import ImageEditorControls from './components/ImageEditorControls.vue'

  // Composables
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import { useCanvasRenderer } from './composables/useCanvasRenderer'
  import { useCropRegions } from './composables/useCropRegions'
  import { useImageEditor } from './composables/useImageEditor'
  import { useMouseInteraction } from './composables/useMouseInteraction'
  import { useTransformations } from './composables/useTransformations'

  // Types
  import type { DocumentAdjustments, ImageEditorEmits, ImageEditorProps } from './types'

  // Translations
  const { t } = useTranslation({
    en: {
      editPage: 'Edit page',
      close: 'Close',
      pageNumber: 'Page {pageNumber}',
      crop: 'Crop',
      rotate: 'Rotate',
      adjust: 'Adjust',
      trim: 'Trim',
      pageImageEditor: 'Page {pageNumber} - Image Editor',
      cancel: 'Cancel',
      applyChanges: 'Apply Changes',
      saveChanges: 'Save changes',
      removePage: 'Remove Page',
      removePageConfirm:
        'Would you like to save page edits? The original page will be replaced, but you will be able to restore it.',
      removeRegion: 'Remove Region',
      discardChanges: 'Discard',
      save: 'Save',
      exitConfirmTitle: 'Save changes?',
      exitConfirmMessage:
        'Would you like to save page edits? The original page will be replaced, but you will be able to restore it.',
      discard: 'Discard',
      cropAppliedSuccessfully: 'Crop applied successfully!',
      page: 'Page'
    },
    de: {
      editPage: 'Seite bearbeiten',
      close: 'Schließen',
      pageNumber: 'Seite {pageNumber}',
      crop: 'Zuschneiden',
      rotate: 'Drehen',
      adjust: 'Anpassen',
      trim: 'Beschneiden',
      pageImageEditor: 'Seite {pageNumber} - Bildbearbeitung',
      cancel: 'Abbrechen',
      applyChanges: 'Änderungen anwenden',
      saveChanges: 'Änderungen speichern',
      removePage: 'Seite entfernen',
      removePageConfirm:
        'Möchten Sie die Seitenbearbeitungen speichern? Die ursprüngliche Seite wird ersetzt, aber Sie können sie wiederherstellen.',
      removeRegion: 'Bereich entfernen',
      discardChanges: 'Verwerfen',
      save: 'Speichern',
      exitConfirmTitle: 'Änderungen speichern?',
      exitConfirmMessage:
        'Möchten Sie die Seitenbearbeitungen speichern? Die ursprüngliche Seite wird ersetzt, aber Sie können sie wiederherstellen.',
      discard: 'Verwerfen',
      cropAppliedSuccessfully: 'Zuschnitt erfolgreich angewendet!',
      page: 'Seite'
    }
  })

  const props = defineProps<ImageEditorProps>()
  const emit = defineEmits<ImageEditorEmits>()
  const { trackEvent } = useMetrics()

  // Destructure props
  const { pageNumber, documentName } = toRefs(props)

  // Canvas component ref
  const canvasComponent = ref<InstanceType<typeof ImageEditorCanvas> | null>(null)
  const zoomedPreviewCanvasRef = ref<HTMLCanvasElement | null>(null)

  // Core image editor state and methods
  const {
    editMode,
    image,
    canvas,
    ctx,
    originalImageDimensions,
    previewScale,
    canvasWidth,
    canvasHeight,
    cropRegions,
    selectedRegionId,
    croppedResults,
    originalCroppedResults,
    baseCroppedResults,
    pristineCroppedResults,
    selectedCroppedRegionId,
    rotation,
    croppedResultsRotation,
    adjustments,
    trimAmounts,
    showCropFeedback,
    showRemoveConfirmation,
    showExitConfirmation,
    imageDisplayParams,
    originalHighResImageData,
    hasAnyAdjustments,
    hasAnyChanges,
    generateId,
    getRegionDisplayName,
    resetEditorState,
    setupCanvas,
    loadImage
  } = useImageEditor(t)

  // Canvas rendering
  const { draw, drawSelectedCroppedRegion, updateMainCanvasWithAllCrops, resetDisplayScale } =
    useCanvasRenderer(
      canvas,
      ctx,
      image,
      editMode,
      cropRegions,
      selectedRegionId,
      croppedResults,
      selectedCroppedRegionId,
      rotation,
      adjustments,
      imageDisplayParams,
      trimAmounts,
      t
    )

  // Crop regions management
  const {
    initCropRegions,
    addNewCropRegion: addNewCropRegionBase,
    removeCropRegion,
    removeRegionByIndex,
    setActiveRegion,
    fitToCanvas,
    fitToImage,
    applyCrop: applyCropBase
  } = useCropRegions(
    canvas,
    image,
    cropRegions,
    selectedRegionId,
    croppedResults,
    originalCroppedResults,
    baseCroppedResults,
    pristineCroppedResults,
    showCropFeedback,
    imageDisplayParams,
    generateId
  )

  // Wrapper functions to include canvas updates
  function addNewCropRegion() {
    // Track crop region added
    trackEvent(MetricEventType.IMAGE_EDITOR_CROP_REGION_ADDED, {
      page_number: props.pageNumber,
      document_name: props.documentName,
      region_count: cropRegions.value.length + 1
    })

    addNewCropRegionBase(draw)
  }

  function applyCrop() {
    // Track crop applied
    trackEvent(MetricEventType.IMAGE_EDITOR_CROP_APPLIED, {
      page_number: props.pageNumber,
      document_name: props.documentName,
      crop_region_count: cropRegions.value.length,
      resulting_pages: cropRegions.value.length
    })

    applyCropBase(updateMainCanvasWithAllCrops)

    // After crops are applied, automatically select "all pages" to show preview
    nextTick(() => {
      if (croppedResults.value.length > 0) {
        selectedCroppedRegionId.value = 'all'
      }
    })
  }

  // Mouse interaction
  const {
    showZoomedPreview,
    zoomedPreviewPosition,
    zoomedPreviewCanvas,
    zoomedPreviewSize,
    updateCanvasRect,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    cleanup: cleanupMouseInteraction
  } = useMouseInteraction(
    canvas,
    editMode,
    cropRegions,
    selectedRegionId,
    canvasWidth,
    canvasHeight,
    setActiveRegion,
    draw,
    image,
    zoomedPreviewCanvasRef,
    imageDisplayParams
  )

  // Transformations
  const transformations = useTransformations(
    croppedResults,
    originalCroppedResults,
    baseCroppedResults,
    pristineCroppedResults,
    selectedCroppedRegionId,
    rotation,
    croppedResultsRotation,
    adjustments,
    trimAmounts,
    draw,
    drawSelectedCroppedRegion,
    updateMainCanvasWithAllCrops
  )

  const {
    rotateLeft: rotateLeftBase,
    rotateRight: rotateRightBase,
    applyCroppedResultAdjustments,
    applyTrimEdges: applyTrimEdgesBase,
    resetRotation: resetRotationBase,
    resetAdjustments: resetAdjustmentsBase,
    resetTrim: resetTrimTransformations,
    resetTransformations,
    rotateImageData,
    adjustImageData,
    trimImageData
  } = transformations

  // Wrapper functions with tracking
  function rotateLeft() {
    trackEvent(MetricEventType.IMAGE_EDITOR_ROTATED, {
      direction: 'left',
      page_number: props.pageNumber,
      document_name: props.documentName,
      has_crops: croppedResults.value.length > 0
    })
    rotateLeftBase()
  }

  function rotateRight() {
    trackEvent(MetricEventType.IMAGE_EDITOR_ROTATED, {
      direction: 'right',
      page_number: props.pageNumber,
      document_name: props.documentName,
      has_crops: croppedResults.value.length > 0
    })
    rotateRightBase()
  }

  function resetRotation() {
    trackEvent(MetricEventType.IMAGE_EDITOR_ROTATION_RESET, {
      page_number: props.pageNumber,
      document_name: props.documentName,
      previous_rotation: rotation.value
    })
    resetRotationBase()
  }

  function resetAdjustments() {
    const currentPageId = selectedCroppedRegionId.value || 'all'
    const currentAdj = adjustments.value[currentPageId]

    trackEvent(MetricEventType.IMAGE_EDITOR_ADJUSTMENTS_RESET, {
      page_number: props.pageNumber,
      document_name: props.documentName,
      had_brightness: currentAdj?.brightness !== 0,
      had_contrast: currentAdj?.contrast !== 0,
      had_gamma: currentAdj?.gamma !== 0,
      had_sharpness: currentAdj?.sharpness !== 0
    })
    resetAdjustmentsBase()
  }

  function applyTrimEdges(regionId: string, trimPercentage: number) {
    trackEvent(MetricEventType.IMAGE_EDITOR_TRIM_APPLIED, {
      page_number: props.pageNumber,
      document_name: props.documentName,
      trim_percentage: trimPercentage,
      region_id: regionId
    })
    applyTrimEdgesBase(regionId, trimPercentage)
  }

  // Connect canvas refs from child component
  watch(
    canvasComponent,
    (component) => {
      if (component) {
        canvas.value = component.canvas
        // Connect the zoomed preview canvas from the component to our ref
        if (component.zoomedPreviewCanvas) {
          zoomedPreviewCanvasRef.value = component.zoomedPreviewCanvas
        }
      }
    },
    { immediate: true }
  )

  // Also watch for when the zoom preview becomes visible and try to connect the canvas
  watch(showZoomedPreview, (isVisible) => {
    if (isVisible && canvasComponent.value) {
      // Always try to reconnect when zoom preview becomes visible
      setTimeout(() => {
        if (canvasComponent.value?.zoomedPreviewCanvas) {
          zoomedPreviewCanvasRef.value = canvasComponent.value.zoomedPreviewCanvas
        }
      }, 50) // Small delay to ensure DOM is updated
    }
  })

  // Lifecycle
  onMounted(() => {
    if (props.imageData) {
      // Delay to ensure container is properly rendered and has final dimensions
      setTimeout(() => {
        resetDisplayScale() // Reset scale only after container is ready
        loadImage(props.imageData, () => {
          initCropRegions()
          draw()
        })
      }, 150)
    }
    // Add resize listener
    window.addEventListener('resize', handleResize)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
    }
    if (typeof cleanupMouseInteraction === 'function') {
      cleanupMouseInteraction()
    }
  })

  // Watchers
  watch(
    () => props.imageData,
    (newImageData) => {
      if (newImageData) {
        resetEditorState()
        // Reset scale only when actually loading the image, not immediately
        loadImage(newImageData, () => {
          resetDisplayScale() // Reset scale after image loads, before drawing
          initCropRegions()
          draw()
        })
      }
    },
    { immediate: true }
  )

  watch(
    () => props.isOpen,
    (isOpen, wasOpen) => {
      if (isOpen && !wasOpen) {
        // Track image editor opened
        trackEvent(MetricEventType.IMAGE_EDITOR_OPENED, {
          page_number: props.pageNumber,
          document_name: props.documentName
        })

        resetEditorState()
        if (props.imageData && props.imageData.length > 0) {
          // Small delay to ensure modal and container are properly rendered
          setTimeout(() => {
            resetDisplayScale() // Reset scale after delay, when container is ready
            loadImage(props.imageData as string, () => {
              initCropRegions()
              draw()
            })
          }, 100)
        }
      } else if (!isOpen && wasOpen) {
        // Track image editor closed
        trackEvent(MetricEventType.IMAGE_EDITOR_CLOSED, {
          page_number: props.pageNumber,
          document_name: props.documentName,
          had_changes: hasAnyChanges.value
        })

        // Clean up when modal closes
        croppedResults.value = []
        originalCroppedResults.value = []
        showCropFeedback.value = false
        selectedCroppedRegionId.value = null
      }
    }
  )

  // Watch for edit mode changes to auto-select regions
  watch(editMode, (newMode, oldMode) => {
    // Track mode change
    if (oldMode && newMode !== oldMode) {
      trackEvent(MetricEventType.IMAGE_EDITOR_MODE_CHANGED, {
        from_mode: oldMode,
        to_mode: newMode,
        page_number: props.pageNumber,
        document_name: props.documentName
      })
    }

    if (newMode === 'rotate') {
      if (croppedResults.value.length > 0 && !selectedCroppedRegionId.value) {
        if (croppedResults.value.length === 1) {
          selectedCroppedRegionId.value = croppedResults.value[0].regionId
        } else {
          selectedCroppedRegionId.value = 'all'
        }
      }
    } else if (newMode === 'adjust') {
      if (
        croppedResults.value.length > 0 &&
        !selectedCroppedRegionId.value &&
        rotation.value === 0
      ) {
        if (croppedResults.value.length === 1) {
          selectedCroppedRegionId.value = croppedResults.value[0].regionId
        } else {
          selectedCroppedRegionId.value = 'all'
        }
      }
    } else if (newMode === 'trim') {
      if (croppedResults.value.length > 0 && !selectedCroppedRegionId.value) {
        if (croppedResults.value.length === 1) {
          selectedCroppedRegionId.value = croppedResults.value[0].regionId
        } else {
          selectedCroppedRegionId.value = 'all'
        }
      }
    } else if (newMode === 'crop') {
      if (croppedResults.value.length > 0) {
        // If crops have been applied, default to "all pages" to show preview of all cropped pages
        selectedCroppedRegionId.value = 'all'
      } else {
        // If no crops applied, clear selection to show original image with crop regions
        selectedCroppedRegionId.value = null
      }
    }

    nextTick(() => {
      draw()
    })
  })

  // Watch for selected region changes to redraw
  watch(selectedCroppedRegionId, () => {
    nextTick(() => {
      draw()
    })
  })

  // Watch for crop region selection changes to redraw
  watch(selectedRegionId, () => {
    nextTick(() => {
      draw()
    })
  })

  // Watch for crop regions changes to redraw
  watch(
    cropRegions,
    () => {
      nextTick(() => {
        draw()
      })
    },
    { deep: true }
  )

  // Methods
  let resizeTimeout: ReturnType<typeof setTimeout> | null = null

  function handleResize() {
    // Debounce resize events to avoid excessive recalculations
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
    }

    resizeTimeout = setTimeout(() => {
      if (image.value && canvas.value) {
        // Store current crop regions and canvas dimensions before resize
        const existingRegions = [...cropRegions.value]
        const hadCroppedResults = croppedResults.value.length > 0
        const oldCanvasWidth = canvasWidth.value
        const oldCanvasHeight = canvasHeight.value

        // Recalculate canvas size based on new viewport
        setupCanvas()

        // If we had existing crop regions and no cropped results, preserve and scale them
        if (
          existingRegions.length > 0 &&
          !hadCroppedResults &&
          oldCanvasWidth > 0 &&
          oldCanvasHeight > 0
        ) {
          // Calculate scaling factors based on canvas size change
          const scaleX = canvasWidth.value / oldCanvasWidth
          const scaleY = canvasHeight.value / oldCanvasHeight

          // Scale existing crop regions proportionally
          cropRegions.value = existingRegions.map((region) => ({
            ...region,
            vertices: region.vertices.map((vertex) => ({
              x: Math.max(0, Math.min(canvasWidth.value, vertex.x * scaleX)),
              y: Math.max(0, Math.min(canvasHeight.value, vertex.y * scaleY))
            }))
          }))
        } else if (!hadCroppedResults) {
          // No existing regions or they were invalid, initialize new ones
          initCropRegions()
        }

        draw()
      }
      resizeTimeout = null
    }, 100) // 100ms debounce for more responsive feel
  }

  function handleRemoveRegion() {
    if (selectedRegionId.value) {
      // Track crop region removed
      trackEvent(MetricEventType.IMAGE_EDITOR_CROP_REGION_REMOVED, {
        page_number: props.pageNumber,
        document_name: props.documentName,
        remaining_regions: cropRegions.value.length - 1
      })

      removeCropRegion(selectedRegionId.value, selectedCroppedRegionId)
      showRemoveConfirmation.value = false
    }
  }

  function handleRemoveRegionByIndex(index: number) {
    const regionToRemove = cropRegions.value[index]
    if (regionToRemove) {
      // Track crop region removed
      trackEvent(MetricEventType.IMAGE_EDITOR_CROP_REGION_REMOVED, {
        page_number: props.pageNumber,
        document_name: props.documentName,
        remaining_regions: cropRegions.value.length - 1
      })

      removeCropRegion(regionToRemove.id, selectedCroppedRegionId)
    }
  }

  function handleAdjustmentChange() {
    if (croppedResults.value.length > 0 && selectedCroppedRegionId.value) {
      applyCroppedResultAdjustments()
    } else {
      draw()
    }
  }

  function updateAdjustment(key: keyof DocumentAdjustments, value: number) {
    const currentPageId = selectedCroppedRegionId.value || 'all'

    // Initialize adjustments for this page if it doesn't exist
    if (!adjustments.value[currentPageId]) {
      adjustments.value[currentPageId] = {
        brightness: 0,
        contrast: 0,
        gamma: 0,
        sharpness: 0,
        saturation: 0
      }
    }

    // Track adjustment changed (debounced tracking - only significant changes)
    const oldValue = adjustments.value[currentPageId][key]
    const diff = Math.abs(value - oldValue)
    if (diff >= 5) {
      // Only track significant changes (every 5 units)
      trackEvent(MetricEventType.IMAGE_EDITOR_ADJUSTMENT_CHANGED, {
        adjustment_type: key,
        value: value,
        page_number: props.pageNumber,
        document_name: props.documentName,
        apply_to: currentPageId
      })
    }

    // Update the specific adjustment for the current page
    adjustments.value[currentPageId][key] = value
    handleAdjustmentChange()
  }

  function resetCrop() {
    // Track crop reset
    trackEvent(MetricEventType.IMAGE_EDITOR_CROP_RESET, {
      page_number: props.pageNumber,
      document_name: props.documentName,
      had_crops: croppedResults.value.length > 0
    })

    croppedResults.value = []
    originalCroppedResults.value = []
    baseCroppedResults.value = []

    const newRegionId = generateId()
    cropRegions.value = [
      {
        id: newRegionId,
        vertices: [
          { x: 50, y: 50 },
          { x: 250, y: 50 },
          { x: 250, y: 250 },
          { x: 50, y: 250 }
        ],
        isActive: true
      }
    ]

    // Set the new region as selected to show the handles
    selectedRegionId.value = newRegionId

    handleAdjustmentChange()
  }

  function resetTrim() {
    // Track trim reset
    trackEvent(MetricEventType.IMAGE_EDITOR_TRIM_RESET, {
      page_number: props.pageNumber,
      document_name: props.documentName
    })

    resetTrimTransformations()
  }

  function handleSetActiveRegion(regionId: string) {
    setActiveRegion(regionId)
    // Also update selectedCroppedRegionId for consistent selection behavior
    if (croppedResults.value.length > 0) {
      selectedCroppedRegionId.value = regionId
    }
  }

  function resetCropRegions() {
    croppedResults.value = []
    originalCroppedResults.value = []
    baseCroppedResults.value = []
    selectedCroppedRegionId.value = null
    rotation.value = 0
    adjustments.value = {}
    croppedResultsRotation.value = 0
    trimAmounts.value = {}
    initCropRegions()
    draw()
  }

  async function applyChanges() {
    // Track changes applied with detailed operation info
    const operations: string[] = []
    if (croppedResults.value.length > 0) operations.push('crop')
    if (rotation.value !== 0) operations.push('rotate')
    if (hasAnyAdjustments.value) operations.push('adjust')
    if (Object.values(trimAmounts.value).some((v) => v > 0)) operations.push('trim')

    trackEvent(MetricEventType.IMAGE_EDITOR_CHANGES_APPLIED, {
      page_number: props.pageNumber,
      document_name: props.documentName,
      operation_types: operations,
      crop_count: croppedResults.value.length,
      rotation_degrees: rotation.value,
      has_adjustments: hasAnyAdjustments.value,
      has_trim: Object.values(trimAmounts.value).some((v) => v > 0)
    })

    if (croppedResults.value.length > 0) {
      // When we have cropped results, use them for PDF replacement
      emit('replace-pdf-page', {
        pageNumber: props.pageNumber,
        croppedPages: croppedResults.value
      })
      emit('apply', croppedResults.value)
    } else if (hasAnyChanges.value && originalHighResImageData.value) {
      // When we have transformations without cropping, apply them to the original high-resolution image
      console.log('Applying transformations to high-resolution image data')

      try {
        // Start with the original high-resolution image data from PDF.js
        let transformedImageData = originalHighResImageData.value

        console.log('Using original high-res image data for transformations:', {
          dataLength: transformedImageData.length,
          hasRotation: rotation.value !== 0,
          hasAdjustments: !!adjustments.value['all'],
          hasTrim: (trimAmounts.value['all'] || 0) > 0
        })

        // Apply rotation if needed
        if (rotation.value !== 0) {
          transformedImageData = await rotateImageData(transformedImageData, rotation.value)
        }

        // Apply adjustments if needed
        const allAdjustments = adjustments.value['all']
        if (
          allAdjustments &&
          (Number(allAdjustments.brightness) !== 0 ||
            Number(allAdjustments.contrast) !== 0 ||
            Number(allAdjustments.gamma) !== 0 ||
            Number(allAdjustments.sharpness) !== 0)
        ) {
          transformedImageData = await adjustImageData(
            transformedImageData,
            Number(allAdjustments.brightness),
            Number(allAdjustments.contrast),
            Number(allAdjustments.gamma),
            Number(allAdjustments.sharpness),
            Number(allAdjustments.saturation)
          )
        }

        // Apply trimming if needed
        // Check for both 'all' and 'original' trim amounts (original is used when no crop regions exist)
        const allTrimAmount = trimAmounts.value['all'] || trimAmounts.value['original'] || 0
        if (allTrimAmount > 0) {
          transformedImageData = await trimImageData(transformedImageData, allTrimAmount)
        }

        // Create a single cropped result that represents the full page with transformations
        const fullPageResult = {
          id: 'full-page',
          regionId: 'full-page',
          imageData: transformedImageData
        }

        console.log('High-resolution transformations applied successfully')

        // Emit both events to ensure PDF gets updated
        emit('replace-pdf-page', {
          pageNumber: props.pageNumber,
          croppedPages: [fullPageResult]
        })
        emit('apply', transformedImageData)
      } catch (error) {
        console.error('Error applying transformations to high-resolution image:', error)
        // Fallback to canvas capture if transformation fails
        if (canvas.value) {
          const editedImageData = canvas.value.toDataURL('image/png', 1.0)
          const fullPageResult = {
            id: 'full-page',
            regionId: 'full-page',
            imageData: editedImageData
          }

          emit('replace-pdf-page', {
            pageNumber: props.pageNumber,
            croppedPages: [fullPageResult]
          })
          emit('apply', editedImageData)
        }
      }
    }
  }

  function closeEditor() {
    if (hasAnyChanges.value) {
      showExitConfirmation.value = true
    } else {
      emit('close')
    }
  }

  function handleExitDiscard() {
    // Track changes discarded
    trackEvent(MetricEventType.IMAGE_EDITOR_CHANGES_DISCARDED, {
      page_number: props.pageNumber,
      document_name: props.documentName,
      had_crops: croppedResults.value.length > 0,
      had_rotation: rotation.value !== 0,
      had_adjustments: hasAnyAdjustments.value
    })

    showExitConfirmation.value = false
    emit('close')
  }

  function handleExitSave() {
    showExitConfirmation.value = false
    applyChanges()
  }
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[80] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm"
  >
    <div
      class="flex h-[95vh] w-[60vw] flex-col overflow-hidden rounded-lg bg-white shadow-lg"
      @click.stop
    >
      <!-- Header -->
      <div class="flex h-16 shrink-0 items-center justify-between border-b border-gray-200 px-6">
        <h1 class="text-xl font-semibold text-gray-900">{{ t('editPage') }}</h1>
        <button
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100
            hover:text-gray-600"
          @click="closeEditor"
        >
          <span class="sr-only">{{ t('close') }}</span>
          <X class="h-5 w-5" />
        </button>
      </div>

      <!-- Mode Tabs -->
      <div class="border-b border-gray-200 px-6 py-4">
        <div class="flex gap-1 rounded-lg bg-gray-100 p-1">
          <button
            :class="[
              `flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium
              transition-all`,
              editMode === 'trim'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            ]"
            @click="editMode = 'trim'"
          >
            <Scissors class="h-4 w-4" />
            {{ t('trim') }}
          </button>
          <button
            :class="[
              `flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium
              transition-all`,
              editMode === 'crop'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            ]"
            @click="editMode = 'crop'"
          >
            <CropIcon />
            {{ t('crop') }}
          </button>
          <button
            :class="[
              `flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium
              transition-all`,
              editMode === 'rotate'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            ]"
            @click="editMode = 'rotate'"
          >
            <RotateCwSquare class="h-4 w-4" />
            {{ t('rotate') }}
          </button>
          <button
            :class="[
              `flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium
              transition-all`,
              editMode === 'adjust'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            ]"
            @click="editMode = 'adjust'"
          >
            <SlidersHorizontal class="h-4 w-4" />
            {{ t('adjust') }}
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="mx-4 mb-4 flex flex-1 overflow-hidden rounded-lg border border-gray-200">
        <!-- Left Sidebar Controls -->
        <div class="flex w-64 flex-col border-r border-gray-200">
          <ImageEditorControls
            :edit-mode="editMode"
            :crop-regions="cropRegions"
            :cropped-results="croppedResults"
            :selected-cropped-region-id="selectedCroppedRegionId"
            :rotation="rotation"
            :cropped-results-rotation="croppedResultsRotation"
            :adjustments="adjustments"
            :trim-amounts="trimAmounts"
            :has-crops-applied="croppedResults.length > 0"
            @update:selected-cropped-region-id="selectedCroppedRegionId = $event"
            @set-active-region="handleSetActiveRegion"
            @remove-region-by-index="handleRemoveRegionByIndex"
            @add-new-crop-region="addNewCropRegion"
            @apply-crop="applyCrop"
            @reset-crop="resetCrop"
            @rotate-left="rotateLeft"
            @rotate-right="rotateRight"
            @reset-rotation="resetRotation"
            @update-adjustment="updateAdjustment"
            @reset-adjustments="resetAdjustments"
            @apply-trim-edges="applyTrimEdges"
            @reset-trim="resetTrim"
          />
        </div>

        <!-- Right Canvas Area -->
        <div class="flex flex-1 overflow-auto bg-gray-50 p-4">
          <ImageEditorCanvas
            ref="canvasComponent"
            :show-zoomed-preview="showZoomedPreview"
            :zoomed-preview-position="zoomedPreviewPosition"
            :zoomed-preview-size="zoomedPreviewSize"
            @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
          />
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex h-16 shrink-0 items-center justify-between border-t border-gray-200 bg-white px-6"
      >
        <div class="text-sm text-gray-600">
          {{ t('pageNumber', { pageNumber }) }}{{ documentName ? ` – ${documentName}` : '' }}
        </div>
        <div class="flex items-center space-x-3">
          <button
            class="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            @click="closeEditor"
          >
            {{ t('cancel') }}
          </button>
          <button
            :disabled="!hasAnyChanges"
            class="rounded px-4 py-2 text-sm font-medium transition-all"
            :class="
              hasAnyChanges
                ? 'bg-primary text-white hover:bg-primary-hover'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            "
            @click="applyChanges"
          >
            {{ t('saveChanges') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Remove Region Confirmation Dialog -->
    <div
      v-if="showRemoveConfirmation"
      class="fixed inset-0 z-[90] flex items-center justify-center bg-black bg-opacity-50"
      @click="showRemoveConfirmation = false"
    >
      <div
        class="w-[600px] rounded-xl bg-white p-6 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-1
          outline-offset-[-1px] outline-gray-300"
        @click.stop
      >
        <!-- Header -->
        <div class="flex flex-col items-start justify-start gap-6">
          <div class="flex flex-col items-start justify-start gap-2 self-stretch">
            <div class="inline-flex items-center justify-between self-stretch">
              <div class="font-['Inter'] text-xl font-semibold leading-loose text-gray-800">
                {{ t('exitConfirmTitle') }}
              </div>
              <button
                type="button"
                class="relative flex h-6 w-6 items-center justify-center overflow-hidden text-gray-400 hover:text-gray-600"
                @click="showRemoveConfirmation = false"
              >
                <span class="sr-only">{{ t('close') }}</span>
                <X class="h-5 w-5" />
              </button>
            </div>
            <div
              class="self-stretch font-['Inter'] text-base font-normal leading-normal text-gray-800"
            >
              {{ t('exitConfirmMessage') }}
            </div>
          </div>

          <!-- Footer -->
          <div class="inline-flex items-start justify-end gap-4 self-stretch">
            <button
              class="flex flex-1 items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 outline outline-1
                outline-offset-[-1px] outline-gray-300 hover:bg-gray-50"
              @click="showRemoveConfirmation = false"
            >
              <div class="font-['Inter'] text-base font-medium leading-normal text-gray-800">
                {{ t('discard') }}
              </div>
            </button>
            <button
              class="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 hover:bg-primary-hover"
              @click="handleRemoveRegion"
            >
              <div class="font-['Inter'] text-base font-medium leading-normal text-white">
                {{ t('save') }}
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Exit Confirmation Dialog -->
    <div
      v-if="showExitConfirmation"
      class="fixed inset-0 z-[90] flex items-center justify-center bg-black bg-opacity-50"
      @click="showExitConfirmation = false"
    >
      <div
        class="w-[600px] rounded-xl bg-white p-6 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-1
          outline-offset-[-1px] outline-gray-300"
        @click.stop
      >
        <!-- Header -->
        <div class="flex flex-col items-start justify-start gap-6">
          <div class="flex flex-col items-start justify-start gap-2 self-stretch">
            <div class="inline-flex items-center justify-between self-stretch">
              <div class="font-['Inter'] text-xl font-semibold leading-loose text-gray-800">
                {{ t('exitConfirmTitle') }}
              </div>
              <button
                type="button"
                class="relative flex h-6 w-6 items-center justify-center overflow-hidden text-gray-400 hover:text-gray-600"
                @click="showExitConfirmation = false"
              >
                <span class="sr-only">{{ t('close') }}</span>
                <X class="h-5 w-5" />
              </button>
            </div>
            <div
              class="self-stretch font-['Inter'] text-base font-normal leading-normal text-gray-800"
            >
              {{ t('exitConfirmMessage') }}
            </div>
          </div>

          <!-- Footer -->
          <div class="inline-flex items-start justify-end gap-4 self-stretch">
            <button
              class="flex flex-1 items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 outline outline-1
                outline-offset-[-1px] outline-gray-300 hover:bg-gray-50"
              @click="handleExitDiscard"
            >
              <div class="font-['Inter'] text-base font-medium leading-normal text-gray-800">
                {{ t('discard') }}
              </div>
            </button>
            <button
              class="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 hover:bg-primary-hover"
              @click="handleExitSave"
            >
              <div class="font-['Inter'] text-base font-medium leading-normal text-white">
                {{ t('save') }}
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Crop success feedback -->
    <div
      v-if="showCropFeedback"
      class="fixed bottom-4 right-4 z-[90] rounded-lg border border-green-200 bg-green-50 p-4 shadow-lg"
    >
      <div class="flex items-center space-x-2">
        <div class="h-3 w-3 rounded-full bg-green-500"></div>
        <span class="text-sm font-medium text-green-800">{{ t('cropAppliedSuccessfully') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
  /* Enhanced range slider styling - Override Tailwind Forms plugin */
  input[type='range'] {
    -webkit-appearance: none !important;
    appearance: none !important;
    height: 6px !important;
    border-radius: 3px !important;
    background: #e5e7eb !important;
    outline: none !important;
    padding: 0 !important;
  }

  input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none !important;
    appearance: none !important;
    width: 18px !important;
    height: 18px !important;
    border-radius: 50% !important;
    background: hsl(271, 81%, 56%) !important;
    cursor: pointer !important;
    border: none !important;
    margin-top: -6px !important;
  }

  input[type='range']::-moz-range-thumb {
    width: 18px !important;
    height: 18px !important;
    border-radius: 50% !important;
    background: hsl(271, 81%, 56%) !important;
    cursor: pointer !important;
    border: none !important;
    -moz-appearance: none !important;
  }

  input[type='range']::-webkit-slider-runnable-track {
    height: 6px !important;
    border-radius: 3px !important;
    background: #e5e7eb !important;
    border: none !important;
  }

  input[type='range']::-moz-range-track {
    height: 6px !important;
    border-radius: 3px !important;
    background: #e5e7eb !important;
    border: none !important;
  }
</style>
